import type { DocPage } from '../types'
import { media } from '../media'


export const mapView: DocPage = {
  id: 'map-view',
  title: 'Map view & attribute table',
  description:
    'Use the Source Tree/Map toggle, draw spatial filters, control layers and basemap, and inspect features in the Attribute Table before transfer.',
  category: 'Workspace',
  sections: [
    { id: 'toggle', title: 'Tree / Map toggle' },
    { id: 'draw', title: 'Draw tools' },
    { id: 'inclusion', title: 'Inclusion modes' },
    { id: 'multi', title: 'Multi-select and clear' },
    { id: 'basemap-layers', title: 'Basemap and layers panel' },
    { id: 'zoom', title: 'Zoom and refresh' },
    { id: 'sampling', title: 'Sampling message' },
    { id: 'hidden', title: 'Hidden layers and transfer' },
    { id: 'epsg', title: 'Override EPSG (CAD)' },
    { id: 'attribute-table', title: 'Attribute Table' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'toggle',
      text: 'Tree / Map toggle',
    },
    {
      type: 'paragraph',
      text: 'On Source, when the connected format supports a spatial overview, switch between Tree and Map. Formats that commonly expose Map include PostGIS, Smallworld, and AutoCAD. Tree shows the hierarchical model of collections and attributes; Map shows geometries for overview, spatial selection, and layer visibility.',
    },
    {
      type: 'figure',
      src: media.mapView,
      alt: 'Source map view with draw tools and layers',
      caption:
        'Figure: Source Map view with spatial overview, draw tools, inclusion mode, and layer controls.',
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Source only',
      text: 'Spatial overview and area filters are driven from the Source side. Target remains a model/configuration panel for write destination settings.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'draw',
      text: 'Draw tools',
    },
    {
      type: 'paragraph',
      text: 'Use Select area / Selection shape tools to define one or more filter geometries. Tools and interaction hints:',
    },
    {
      type: 'table',
      headers: ['Tool', 'How to draw'],
      rows: [
        ['Rectangle', 'Drag a rectangle on the map, then release.'],
        [
          'Polygon',
          'Click vertices to outline the area. Press C or right-click to close; Esc cancels.',
        ],
        ['Freehand', 'Press, drag the freehand path, then release.'],
        ['Radius', 'Click the center, drag to set the radius, then release.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'inclusion',
      text: 'Inclusion modes',
    },
    {
      type: 'paragraph',
      text: 'Inclusion mode controls how selected areas interact with source features during transfer.',
    },
    {
      type: 'table',
      headers: ['Mode', 'Description'],
      rows: [
        ['Clip', 'Cut geometries to the selection (default).'],
        ['Intersect', 'Keep whole features that touch the selection.'],
        ['Within', 'Keep whole features only if fully inside.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'multi',
      text: 'Multi-select and clear',
    },
    {
      type: 'list',
      items: [
        'Multi-select / Multi-select on — allow multiple area geometries at once; the UI shows {{count}} areas when several exist.',
        'Remove area — delete one selected area from the set.',
        'Clear selection — remove all drawn filter areas.',
        'The spatial area filter status also appears on Transfer Review before you run.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'basemap-layers',
      text: 'Basemap and layers panel',
    },
    {
      type: 'table',
      headers: ['Control', 'Purpose'],
      rows: [
        ['Hide basemap / Show basemap', 'Toggle the reference basemap under your data.'],
        ['Layers', 'Open the layers panel for collection visibility and geometry types.'],
        ['Search layers…', 'Filter the layer list by name.'],
        ['Show all / Hide all', 'Bulk visibility for listed layers.'],
        ['{{count}} hidden', 'Summary of layers currently hidden.'],
        ['Point / Line / Area / Text', 'Geometry-type groupings in the layer list where applicable.'],
        ['Zoom to layer', 'Frame the map on one layer’s extent.'],
        ['Show only this layer', 'Isolate a single layer visually.'],
        ['Open attribute table', 'Inspect tabular attributes for the layer (see below).'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'zoom',
      text: 'Zoom and refresh',
    },
    {
      type: 'list',
      items: [
        'Zoom to extent — fit the map to the loaded overview data.',
        'Refresh — reload overview geometries.',
        'Stop loading map — cancel an in-progress overview fetch on large datasets.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'sampling',
      text: 'Sampling message',
    },
    {
      type: 'paragraph',
      text: 'For large datasets the overview may sample features. The map can show messages such as Showing {{sampled}} of {{total}}, sampled, and area filter on. Sampling affects what you see for overview only; the spatial filter you draw still feeds the transfer query according to inclusion mode.',
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Do not confuse sample with transfer count',
      text: 'A sampled overview does not mean only sampled rows will transfer. Use Transfer Review and the logger for actual transferred record counts.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'hidden',
      text: 'Hidden layers and transfer',
    },
    {
      type: 'paragraph',
      text: 'Layers you hide in the Map layers panel are excluded from transfer. Transfer Review reports Map layer visibility with text such as N collection(s) hidden (excluded from transfer). Hidden map layer IDs override the tree selection allow-list for those collections.',
    },
    {
      type: 'callout',
      variant: 'warning',
      title: 'Visibility matters',
      text: 'Before Transfer, open Layers and confirm required collections are visible. Accidental Hide all or Show only this layer can silently drop collections from the run.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'epsg',
      text: 'Override EPSG (CAD)',
    },
    {
      type: 'paragraph',
      text: 'AutoCAD sources often need an explicit CRS. On the CAD connection form, set EPSG Code (stored as override_epsg). Use Select EPSG code and Search by code or name. Without a correct Override EPSG, map overview and spatial filters may misplace CAD geometry relative to the basemap and other formats.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'attribute-table',
      text: 'Attribute Table',
    },
    {
      type: 'paragraph',
      text: 'From Source, open an attribute table for a collection/layer (for example from Map layers or the table tab next to Tree / Map). The table shows feature attributes with search, per-column filters, sorting, and a row count such as 6 of 6. For AutoCAD sources you typically see fields like layer_name, linetype, rgb, aci, entity_name, and related CAD properties.',
    },
    {
      type: 'figure',
      src: media.attributeTable,
      alt: 'Source attribute table for AutoCAD features',
      caption:
        'Figure: Source Attribute Table — Tree / Map / table tabs, Search attributes…, column filters, and End of table when all rows are loaded.',
    },
    {
      type: 'table',
      headers: ['Control', 'Behavior'],
      rows: [
        ['Tree / Map / table tabs', 'Switch between model tree, map overview, and an open attribute table tab (closable).'],
        ['Search attributes…', 'Global text search across visible attribute values.'],
        ['Column sort / filter icons', 'Sort a column or open a per-column filter (Contains…).'],
        ['Apply / Clear filters', 'Apply or remove column filters when the filter UI is open.'],
        ['N of M', 'Shows how many rows are loaded vs total for the current view.'],
        ['End of table', 'All available rows for this page/window have been shown.'],
        ['Loading more…', 'Shown while fetching the next page of rows on larger tables.'],
        ['No features found', 'Empty result for the current search/filters.'],
        ['Failed to load attribute table', 'Load error — retry or check the source connection.'],
      ],
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Related topics',
      text: 'Model tree, Feature Options, and Filters are covered under Model tree & filters. Spatial filter status also appears on Transfer Review.',
    },
  ],
}
